import { useCallback, useEffect, useState } from "react";
import Button from "@mui/material/Button";
import CircularProgress from "@mui/material/CircularProgress";
import IconButton from "@mui/material/IconButton";
import TextField from "@mui/material/TextField";
import Tooltip from "@mui/material/Tooltip";
import { FolderPlus, Save, Share2, Tags, Trash2 } from "lucide-react";
import type { PostCategory } from "../../shared/contracts";
import { ShareDialog } from "./ShareDialog";
import { api } from "../lib/api";
import { toast } from "../lib/toast";
import { StatePanel } from "./StatePanel";

export function CategoryManager() {
  const [items, setItems] = useState<PostCategory[]>([]);
  const [drafts, setDrafts] = useState<Record<string, string>>({});
  const [newName, setNewName] = useState("");
  const [loading, setLoading] = useState(true);
  const [busyId, setBusyId] = useState<string | null>(null);
  const [shareCategory, setShareCategory] = useState<PostCategory | null>(null);
  const load = useCallback(async () => {
    try { setItems((await api<{ items: PostCategory[] }>("/users/me/categories")).items); }
    finally { setLoading(false); }
  }, []);
  useEffect(() => { void load(); }, [load]);

  const create = async () => {
    if (!newName.trim()) return;
    setBusyId("new");
    try {
      const { category } = await api<{ category: PostCategory }>("/users/me/categories", { method: "POST", body: JSON.stringify({ name: newName.trim() }) });
      setItems((current) => [...current, category]); setNewName(""); toast("分类已创建", "success");
    } catch (error) { toast(error instanceof Error ? error.message : "分类创建失败", "danger"); }
    finally { setBusyId(null); }
  };

  const rename = async (category: PostCategory) => {
    const name = (drafts[category.id] ?? category.name).trim();
    if (!name || name === category.name) return;
    setBusyId(category.id);
    try {
      const result = await api<{ category: PostCategory }>(`/users/me/categories/${category.id}`, { method: "PATCH", body: JSON.stringify({ name }) });
      setItems((current) => current.map((item) => item.id === category.id ? result.category : item));
      setDrafts((current) => ({ ...current, [category.id]: "" })); toast("分类名称已更新", "success");
    } catch (error) { toast(error instanceof Error ? error.message : "分类更新失败", "danger"); }
    finally { setBusyId(null); }
  };

  const remove = async (category: PostCategory) => {
    setBusyId(category.id);
    try {
      await api(`/users/me/categories/${category.id}`, { method: "DELETE" });
      setItems((current) => current.filter((item) => item.id !== category.id)); toast("分类已删除，原动态保留为未分类");
    } catch (error) { toast(error instanceof Error ? error.message : "分类删除失败", "danger"); }
    finally { setBusyId(null); }
  };

  return <section className="account-section category-manager"><header><Tags /><div><h2>内容分类</h2><p>创建自己的分类名称，用于整理发布内容和个人主页筛选。</p></div></header>
    <div className="category-block category-create-block">
      <div className="category-block-heading"><div><h3>新建分类</h3><p>为发布内容添加一个易于识别的分类名称。</p></div></div>
      <div className="category-create"><TextField label="新分类" value={newName} onChange={(event) => setNewName(event.target.value)} placeholder="例如：角色养成、实拍收藏" slotProps={{ htmlInput: { maxLength: 24 } }} /><Button variant="contained" type="button" disabled={busyId === "new" || !newName.trim()} startIcon={busyId === "new" ? <CircularProgress color="inherit" size={16} /> : <FolderPlus />} onClick={() => void create()}>创建分类</Button></div>
    </div>
    <div className="category-block category-list-block">
      <div className="category-block-heading"><div><h3>自定义分类</h3><p>编辑或删除已创建的分类，原有动态会保留。</p></div>{!loading ? <span className="category-count">{items.length} 个分类</span> : null}</div>
      {loading ? <StatePanel kind="loading" /> : items.length === 0 ? <StatePanel kind="empty" title="还没有自定义分类" description="创建后可在发布和编辑动态时选择。" /> : <div className="category-list">{items.map((category) => <div className="category-row" key={category.id}><TextField label="分类名称" value={drafts[category.id] ?? category.name} onChange={(event) => setDrafts((current) => ({ ...current, [category.id]: event.target.value }))} slotProps={{ htmlInput: { maxLength: 24 } }} /><span className="category-post-count">{category.postCount} 条内容</span><Tooltip title="分享分类"><span><IconButton type="button" disabled={!category.shareToken} aria-label={`分享${category.name}分类中的公开动态`} onClick={() => setShareCategory(category)}><Share2 /></IconButton></span></Tooltip><Tooltip title="保存名称"><span><IconButton type="button" disabled={busyId === category.id || !(drafts[category.id] ?? "").trim() || drafts[category.id] === category.name} aria-label={`保存${category.name}分类名称`} onClick={() => void rename(category)}><Save /></IconButton></span></Tooltip><Tooltip title="删除分类"><span><IconButton color="error" type="button" disabled={busyId === category.id} aria-label={`删除${category.name}分类`} onClick={() => void remove(category)}><Trash2 /></IconButton></span></Tooltip></div>)}</div>}
    </div>
    {shareCategory?.shareToken ? <ShareDialog open title={`分享「${shareCategory.name}」`} description={`分享该分类中的公开动态，共 ${shareCategory.postCount} 条。私有、待审核或已移入仓库的内容不会对外展示。`} url={`${window.location.origin}/share/categories/${shareCategory.shareToken}`} onClose={() => setShareCategory(null)} /> : null}
  </section>;
}
